import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiUrlService } from './api-url.service';
import {
  PANEL_ACTIVITY_DEFAULT_LIMIT,
  PANEL_ACTIVITY_DEFAULT_TIMEZONE,
  PanelActivityBudgetVolumeResponse,
  PanelActivityDateValue,
  PanelActivityPeriod,
  PanelActivityLogActorFilter,
  PanelActivityBudgetsLogResponse,
  PanelActivityLogResponse,
  PanelActivityRequestsLogResponse,
  PanelActivityRequestVolumeResponse,
  PanelActivityStatusResponse,
  PanelActivityTopClientsResponse,
  PanelRequiredActionsResponse,
  PanelRequiredActionStateUpdateResponse
} from '../models/panel-administracion';

@Injectable({
  providedIn: 'root'
})
export class AdminPanelService {
  constructor(private apiUrl: ApiUrlService, private http: HttpClient) {}

  public getActivityStatus(timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE): Observable<PanelActivityStatusResponse> {
    const params = this.buildParams({ timezone });
    return this.http.get<PanelActivityStatusResponse>(this.apiUrl.adminPanelActivityStatus, { params });
  }

  public getRequestsVolume(
    period: PanelActivityPeriod = 'today',
    date?: PanelActivityDateValue | null,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE
  ): Observable<PanelActivityRequestVolumeResponse> {
    const params = this.buildPeriodParams(period, date, timezone);
    return this.http.get<PanelActivityRequestVolumeResponse>(this.apiUrl.adminPanelRequestsVolume, { params });
  }

  public getBudgetsVolume(
    period: PanelActivityPeriod = 'today',
    date?: PanelActivityDateValue | null,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE
  ): Observable<PanelActivityBudgetVolumeResponse> {
    const params = this.buildPeriodParams(period, date, timezone);
    return this.http.get<PanelActivityBudgetVolumeResponse>(this.apiUrl.adminPanelBudgetsVolume, { params });
  }

  public getRequestsTopClients(
    period: PanelActivityPeriod = 'today',
    date?: PanelActivityDateValue | null,
    limit = PANEL_ACTIVITY_DEFAULT_LIMIT,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE
  ): Observable<PanelActivityTopClientsResponse> {
    const params = this.buildTopClientsParams(period, date, limit, timezone);
    return this.http.get<PanelActivityTopClientsResponse>(this.apiUrl.adminPanelRequestsTopClients, { params });
  }

  public getBudgetsTopClients(
    period: PanelActivityPeriod = 'today',
    date?: PanelActivityDateValue | null,
    limit = PANEL_ACTIVITY_DEFAULT_LIMIT,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE
  ): Observable<PanelActivityTopClientsResponse> {
    const params = this.buildTopClientsParams(period, date, limit, timezone);
    return this.http.get<PanelActivityTopClientsResponse>(this.apiUrl.adminPanelBudgetsTopClients, { params });
  }

  public getRequestsLog(
    cursor?: string | null,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE,
    actorType?: PanelActivityLogActorFilter | null
  ): Observable<PanelActivityRequestsLogResponse> {
    const params = this.buildActivityLogParams(cursor, timezone, actorType);
    return this.http.get<PanelActivityRequestsLogResponse>(this.apiUrl.adminPanelRequestsLog, { params });
  }

  public getBudgetsLog(
    cursor?: string | null,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE,
    actorType?: PanelActivityLogActorFilter | null
  ): Observable<PanelActivityBudgetsLogResponse> {
    const params = this.buildActivityLogParams(cursor, timezone, actorType);
    return this.http.get<PanelActivityBudgetsLogResponse>(this.apiUrl.adminPanelBudgetsLog, { params });
  }

  public getActivityLog(
    domain: 'requests' | 'budgets',
    cursor?: string | null,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE,
    actorType?: PanelActivityLogActorFilter | null
  ): Observable<PanelActivityLogResponse> {
    return domain === 'requests'
      ? this.getRequestsLog(cursor, timezone, actorType)
      : this.getBudgetsLog(cursor, timezone, actorType);
  }

  public getRequiredActions(
    limit = PANEL_ACTIVITY_DEFAULT_LIMIT,
    includeIgnored = false,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE
  ): Observable<PanelRequiredActionsResponse> {
    const params = this.buildParams({
      limit,
      include_ignored: includeIgnored,
      timezone
    });

    return this.http.get<PanelRequiredActionsResponse>(this.apiUrl.adminPanelRequiredActions, { params });
  }

  public resolveRequiredAction(actionKey: string): Observable<PanelRequiredActionStateUpdateResponse> {
    return this.http.put<PanelRequiredActionStateUpdateResponse>(
      this.apiUrl.adminPanelRequiredActionResolve(actionKey),
      null
    );
  }

  public ignoreRequiredAction(actionKey: string): Observable<PanelRequiredActionStateUpdateResponse> {
    return this.http.put<PanelRequiredActionStateUpdateResponse>(
      this.apiUrl.adminPanelRequiredActionIgnore(actionKey),
      null
    );
  }

  public reopenRequiredAction(actionKey: string): Observable<PanelRequiredActionStateUpdateResponse> {
    return this.http.put<PanelRequiredActionStateUpdateResponse>(
      this.apiUrl.adminPanelRequiredActionReopen(actionKey),
      null
    );
  }

  private buildPeriodParams(
    period: PanelActivityPeriod,
    date?: PanelActivityDateValue | null,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE
  ): HttpParams {
    return this.buildParams({
      period,
      date: this.formatDateParam(date),
      timezone
    });
  }

  private buildActivityLogParams(
    cursor?: string | null,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE,
    actorType?: PanelActivityLogActorFilter | null
  ): HttpParams {
    return this.buildParams({
      cursor,
      actor_type: actorType,
      timezone
    });
  }

  private buildTopClientsParams(
    period: PanelActivityPeriod,
    date: PanelActivityDateValue | null | undefined,
    limit: number,
    timezone = PANEL_ACTIVITY_DEFAULT_TIMEZONE
  ): HttpParams {
    return this.buildParams({
      period,
      date: this.formatDateParam(date),
      limit,
      timezone
    });
  }

  private buildParams(params: Record<string, string | number | boolean | null | undefined>): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      httpParams = httpParams.set(key, String(value));
    });

    return httpParams;
  }

  private formatDateParam(date?: PanelActivityDateValue | null): string | undefined {
    if (!date) return undefined;
    if (typeof date === 'string') return date;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
